const userInfoCache = new Map();

const getUserInfo = async (userId) => {
    if (userInfoCache.has(userId)) {
        return userInfoCache.get(userId);
    }

    try {
        const { firestore } = await import('../../config/firebase');
        const userDoc = await firestore.collection('users').doc(userId).get();

        if (userDoc.exists) {
            const userData = userDoc.data();
            const userInfo = {
                id: userId,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                name: userData.name || '',
                email: userData.email || '',
                displayName: userData.displayName || userData.name ||
                    (userData.firstName && userData.lastName ?
                        `${userData.firstName} ${userData.lastName}` : ''),
                role: userData.role || 'user',
                isAdmin: userData.isAdmin || userData.role === 'admin' || userData.role === 'super'
            };

            userInfoCache.set(userId, userInfo);
            setTimeout(() => userInfoCache.delete(userId), 5 * 60 * 1000);

            return userInfo;
        }
    } catch (error) {
    }

    const fallbackInfo = {
        id: userId,
        displayName: 'Utilisateur',
        firstName: '',
        lastName: '',
        name: 'Utilisateur',
        email: '',
        role: 'user',
        isAdmin: false
    };

    userInfoCache.set(userId, fallbackInfo);
    return fallbackInfo;
};

export const SET_EXPERIENCES = 'SET_EXPERIENCES';
export const ADD_EXPERIENCE = 'ADD_EXPERIENCE';
export const UPDATE_EXPERIENCE = 'UPDATE_EXPERIENCE';
export const DELETE_EXPERIENCE = 'DELETE_EXPERIENCE';
export const SET_EXPERIENCE_REACTIONS = 'SET_EXPERIENCE_REACTIONS';
export const ADD_EXPERIENCE_COMMENT = 'ADD_EXPERIENCE_COMMENT';
export const DELETE_EXPERIENCE_COMMENT = 'DELETE_EXPERIENCE_COMMENT';
export const SET_EXPERIENCE_LOADING = 'SET_EXPERIENCE_LOADING';

export const fetchExperiences = () => async dispatch => {
    dispatch(setExperienceLoading(true));

    try {
        try {
            const { firestore } = await import('../../config/firebase');
            const experiencesRef = firestore.collection('experiences');
            const snapshot = await experiencesRef
                .orderBy('createdAt', 'desc')
                .get();

            const experiences = [];
            const experiencePromises = [];

            snapshot.forEach(doc => {
                const experienceData = {
                    id: doc.id,
                    ...doc.data()
                };
                experiences.push(experienceData);

                if (experienceData.author && experienceData.author.id) {
                    experiencePromises.push(
                        getUserInfo(experienceData.author.id).then(userInfo => {
                            experienceData.author = {
                                ...experienceData.author,
                                name: userInfo.displayName || userInfo.name || experienceData.author.name,
                                firstName: userInfo.firstName,
                                lastName: userInfo.lastName,
                                email: userInfo.email
                            };
                        }).catch(error => {
                        })
                    );
                }

                if (experienceData.comments && Array.isArray(experienceData.comments)) {
                    experienceData.comments.forEach(comment => {
                        if (comment.authorId) {
                            experiencePromises.push(
                                getUserInfo(comment.authorId).then(userInfo => {
                                    comment.author = userInfo.displayName || userInfo.name || comment.author;
                                    comment.authorName = userInfo.displayName || userInfo.name;
                                    comment.authorEmail = userInfo.email;
                                }).catch(error => {
                                })
                            );
                        }
                    });
                }
            });

            await Promise.all(experiencePromises);

            dispatch(setExperiences(experiences));

        } catch (firestoreError) {
            const testExperiences = [
                {
                    id: '1',
                    title: 'Mon expérience avec le Paracétamol',
                    content: 'J\'ai utilisé ce médicament pour traiter mes maux de tête...',
                    author: {
                        name: 'Dr. Martin',
                        id: 'user1'
                    },
                    createdAt: new Date().toISOString(),
                    images: [],
                    reactions: {
                        likes: 5,
                        hearts: 2
                    },
                    comments: [
                        {
                            id: 'c1',
                            author: 'Dr. Dupont',
                            content: 'Très intéressant, merci pour le partage !',
                            createdAt: new Date().toISOString()
                        }
                    ]
                }
            ];

            dispatch(setExperiences(testExperiences));
        }

    } catch (error) {
        dispatch(setExperiences([]));
    } finally {
        dispatch(setExperienceLoading(false));
    }
};

export const updateExperience = (experienceId, updatedData) => async (dispatch, getState) => {
    dispatch(setExperienceLoading(true));

    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const experience = state.experiences.experiences.find(exp => exp.id === experienceId);
        const { isAdmin } = await import('../../helpers/permissions');

        if (!experience || (experience.author.id !== currentUser.uid && !isAdmin(currentUser))) {
            throw new Error('Vous n\'avez pas les droits pour modifier cette expérience');
        }

        const updatedExperience = {
            ...experience,
            title: updatedData.title,
            content: updatedData.content,
            lesson: updatedData.lesson || experience.lesson, // Inclure la leçon
            images: updatedData.images || experience.images,
            updatedAt: new Date().toISOString()
        };

        try {
            const { firestore } = await import('../../config/firebase');
            await firestore.collection('experiences').doc(experienceId).update({
                title: updatedData.title,
                content: updatedData.content,
                lesson: updatedData.lesson || experience.lesson, // Inclure la leçon
                images: updatedData.images || experience.images,
                updatedAt: new Date().toISOString()
            });
        } catch (firestoreError) {
        }

        dispatch(updateExperienceAction(updatedExperience));
        return updatedExperience;

    } catch (error) {
        throw error;
    } finally {
        dispatch(setExperienceLoading(false));
    }
};

export const createExperience = (experienceData) => async (dispatch, getState) => {
    dispatch(setExperienceLoading(true));

    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const userInfo = await getUserInfo(currentUser.uid);

        const newExperience = {
            title: experienceData.title,
            content: experienceData.content,
            lesson: experienceData.lesson, // Nouveau champ obligatoire
            author: {
                name: userInfo.displayName || userInfo.name || 'Utilisateur',
                id: currentUser.uid,
                email: userInfo.email || currentUser.email,
                firstName: userInfo.firstName,
                lastName: userInfo.lastName
            },
            createdAt: new Date().toISOString(),
            images: experienceData.images || [],
            reactions: {
                likes: 0,
                hearts: 0
            },
            comments: []
        };

        try {
            const { firestore } = await import('../../config/firebase');
            const docRef = await firestore.collection('experiences').add(newExperience);
            newExperience.id = docRef.id;
        } catch (firestoreError) {
            newExperience.id = 'local_' + Date.now();
        }

        dispatch(addExperience(newExperience));
        return newExperience;

    } catch (error) {
        throw error;
    } finally {
        dispatch(setExperienceLoading(false));
    }
};

export const reactToExperience = (experienceId, reactionType) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        try {
            const { firestore } = await import('../../config/firebase');
            const experienceRef = firestore.collection('experiences').doc(experienceId);

            const increment = require('firebase/firestore').increment || ((n) => n + 1);
            await experienceRef.update({
                [`reactions.${reactionType}`]: increment(1)
            });

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.map(exp => {
            if (exp.id === experienceId) {
                return {
                    ...exp,
                    reactions: {
                        ...exp.reactions,
                        [reactionType]: (exp.reactions[reactionType] || 0) + 1
                    }
                };
            }
            return exp;
        });

        dispatch(setExperiences(updatedExperiences));

    } catch (error) {
        throw error;
    }
};

export const updateComment = (experienceId, commentId, updatedText) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const experience = state.experiences.experiences.find(exp => exp.id === experienceId);
        const comment = experience?.comments?.find(c => c.id === commentId);

        if (!comment || comment.authorId !== currentUser.uid) {
            throw new Error('Vous n\'avez pas les droits pour modifier ce commentaire');
        }

        const updatedComment = {
            ...comment,
            content: updatedText.trim(),
            updatedAt: new Date().toISOString()
        };

        try {
            const { firestore } = await import('../../config/firebase');
            const experienceRef = firestore.collection('experiences').doc(experienceId);

            const updatedComments = experience.comments.map(c =>
                c.id === commentId ? updatedComment : c
            );

            await experienceRef.update({
                comments: updatedComments
            });

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.map(exp => {
            if (exp.id === experienceId) {
                return {
                    ...exp,
                    comments: exp.comments.map(c => c.id === commentId ? updatedComment : c)
                };
            }
            return exp;
        });

        dispatch(setExperiences(updatedExperiences));
        return updatedComment;

    } catch (error) {
        throw error;
    }
};

export const deleteOwnComment = (experienceId, commentId) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const experience = state.experiences.experiences.find(exp => exp.id === experienceId);
        const comment = experience?.comments?.find(c => c.id === commentId);

        if (!comment || comment.authorId !== currentUser.uid) {
            throw new Error('Vous n\'avez pas les droits pour supprimer ce commentaire');
        }

        try {
            const { firestore } = await import('../../config/firebase');
            const experienceRef = firestore.collection('experiences').doc(experienceId);

            const updatedComments = experience.comments.filter(c => c.id !== commentId);

            await experienceRef.update({
                comments: updatedComments
            });

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.map(exp => {
            if (exp.id === experienceId) {
                return {
                    ...exp,
                    comments: exp.comments.filter(c => c.id !== commentId)
                };
            }
            return exp;
        });

        dispatch(setExperiences(updatedExperiences));

    } catch (error) {
        throw error;
    }
};

export const addComment = (experienceId, commentText) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const userInfo = await getUserInfo(currentUser.uid);

        const newComment = {
            id: 'comment_' + Date.now(),
            author: userInfo.displayName || userInfo.name || 'Utilisateur',
            authorId: currentUser.uid,
            authorEmail: userInfo.email || currentUser.email,
            authorName: userInfo.displayName || userInfo.name,
            content: commentText,
            createdAt: new Date().toISOString()
        };

        try {
            const { firestore } = await import('../../config/firebase');
            const experienceRef = firestore.collection('experiences').doc(experienceId);

            await experienceRef.update({
                comments: require('firebase/firestore').arrayUnion ?
                    require('firebase/firestore').arrayUnion(newComment) :
                    [...(state.experiences.experiences.find(exp => exp.id === experienceId)?.comments || []), newComment]
            });

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.map(exp => {
            if (exp.id === experienceId) {
                return {
                    ...exp,
                    comments: [...(exp.comments || []), newComment]
                };
            }
            return exp;
        });

        dispatch(setExperiences(updatedExperiences));
        return newComment;

    } catch (error) {
        throw error;
    }
};

export const deleteOwnExperience = (experienceId) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;

        if (!currentUser) {
            throw new Error('Utilisateur non connecté');
        }

        const experience = state.experiences.experiences.find(exp => exp.id === experienceId);
        if (!experience || experience.author.id !== currentUser.uid) {
            throw new Error('Vous ne pouvez supprimer que vos propres expériences');
        }

        try {
            const { firestore } = await import('../../config/firebase');
            await firestore.collection('experiences').doc(experienceId).delete();
        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.filter(exp => exp.id !== experienceId);
        dispatch(setExperiences(updatedExperiences));

    } catch (error) {
        throw error;
    }
};

export const deleteExperience = (experienceId, reason) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;
        const { isAdmin } = await import('../../helpers/permissions');

        if (!isAdmin(currentUser)) {
            throw new Error('Droits insuffisants');
        }

        try {
            const { firestore } = await import('../../config/firebase');
            await firestore.collection('experiences').doc(experienceId).delete();

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.filter(exp => exp.id !== experienceId);
        dispatch(setExperiences(updatedExperiences));

    } catch (error) {
        throw error;
    }
};

export const deleteComment = (experienceId, commentId, reason) => async (dispatch, getState) => {
    try {
        const state = getState();
        const currentUser = state.user.user || state.user.offlineUser;
        const { isAdmin } = await import('../../helpers/permissions');

        if (!isAdmin(currentUser)) {
            throw new Error('Droits insuffisants');
        }

        try {
            const { firestore } = await import('../../config/firebase');
            const experienceRef = firestore.collection('experiences').doc(experienceId);

            const experience = state.experiences.experiences.find(exp => exp.id === experienceId);
            const updatedComments = experience.comments.filter(comment => comment.id !== commentId);

            await experienceRef.update({
                comments: updatedComments
            });

        } catch (firestoreError) {
        }

        const experiences = state.experiences.experiences;
        const updatedExperiences = experiences.map(exp => {
            if (exp.id === experienceId) {
                return {
                    ...exp,
                    comments: exp.comments.filter(comment => comment.id !== commentId)
                };
            }
            return exp;
        });

        dispatch(setExperiences(updatedExperiences));

    } catch (error) {
        throw error;
    }
};

export const setExperiences = (experiences) => ({
    type: SET_EXPERIENCES,
    payload: experiences
});

export const addExperience = (experience) => ({
    type: ADD_EXPERIENCE,
    payload: experience
});

export const updateExperienceAction = (experience) => ({
    type: UPDATE_EXPERIENCE,
    payload: experience
});

export const deleteExperienceAction = (experienceId) => ({
    type: DELETE_EXPERIENCE,
    payload: experienceId
});

export const setExperienceReactions = (experienceId, reactions) => ({
    type: SET_EXPERIENCE_REACTIONS,
    payload: { experienceId, reactions }
});

export const addExperienceComment = (experienceId, comment) => ({
    type: ADD_EXPERIENCE_COMMENT,
    payload: { experienceId, comment }
});

export const deleteExperienceComment = (experienceId, commentId) => ({
    type: DELETE_EXPERIENCE_COMMENT,
    payload: { experienceId, commentId }
});

export const setExperienceLoading = (loading) => ({
    type: SET_EXPERIENCE_LOADING,
    payload: loading
});